import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Empty } from "./AdminLeads";
import { toast } from "sonner";
import { Trash2, Pencil, Plus } from "lucide-react";

const EMPTY = { text: "", author: "", order: 0 };

export function AdminQuotes() {
  const qc = useQueryClient();
  const { data: quotes = [], isLoading } = useQuery({ queryKey: ["admin-quotes"], queryFn: () => api.get("/quotes").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: k === "order" ? Number(e.target.value) : e.target.value });

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: quotes.length }); setOpen(true); };
  const openEdit = (q) => { setEditing(q.id); setForm(q); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/quotes/${editing}`, form);
      else await api.post("/quotes", form);
      qc.invalidateQueries({ queryKey: ["admin-quotes"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success(editing ? "Quote updated" : "Quote added");
      setOpen(false);
    } catch (err) { toast.error("Could not save quote"); }
  };
  const remove = async (id) => {
    await api.delete(`/quotes/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-quotes"] });
    qc.invalidateQueries({ queryKey: ["quotes"] });
    toast.success("Quote deleted");
  };

  return (
    <div data-testid="admin-quotes">
      <div className="mb-6 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="quote-new-btn" className="rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90">
              <Plus className="mr-2 h-4 w-4" /> New Quote
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl border-border bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold uppercase">{editing ? "Edit" : "New"} Quote</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <Textarea required data-testid="quote-text" className="tool-input min-h-[120px] rounded-none" placeholder="Quote text" value={form.text} onChange={set("text")} />
              <Input data-testid="quote-author" className="tool-input rounded-none" placeholder="Author (optional)" value={form.author} onChange={set("author")} />
              <Input type="number" data-testid="quote-order" className="tool-input rounded-none" placeholder="Order" value={form.order} onChange={set("order")} />
              <Button type="submit" data-testid="quote-save" className="w-full rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90">{editing ? "Update" : "Add"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? <p className="font-mono text-sm text-muted-foreground">Loading…</p>
        : !quotes.length ? <Empty label="No quotes yet." />
        : (
        <div className="grid gap-px border border-border bg-border">
          {quotes.map((q) => (
            <div key={q.id} className="flex items-center justify-between gap-4 bg-background p-5" data-testid={`admin-quote-${q.id}`}>
              <div className="min-w-0">
                <p className="truncate text-foreground">“{q.text}”</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">— {q.author || "Unknown"} · order {q.order}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(q)} data-testid={`quote-edit-${q.id}`} className="text-muted-foreground hover:text-cyan"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(q.id)} data-testid={`quote-delete-${q.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
